/**
 * @file: StatusDotLabel.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { Colors, StatusColors } from '@euler/components/Colors';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { memo } from 'react';
import { StyleProp, Text, TextStyle, View } from 'react-native';

export type StatusTone = 'ok' | 'pending' | 'error' | 'muted';

const kToneColor: Record<StatusTone, string> = {
  ok: StatusColors.Success,
  pending: StatusColors.Warn,
  error: StatusColors.Danger,
  muted: Colors.Gray2,
};

/**
 * A status as a dot plus its label, so the state reads at a glance rather
 * than depending on the text colour alone.
 */
export const StatusDotLabel = memo(
  ({
    tone,
    label,
    labelStyle,
  }: {
    tone: StatusTone;
    label: string;
    /**
     * Restyles the label while the dot keeps the tone. For a row sitting
     * among plain settings cells, where matching its neighbours matters more
     * than colouring the text.
     */
    labelStyle?: StyleProp<TextStyle>;
  }) => {
    const { rpx } = useResponsiveContext();
    const color = kToneColor[tone];
    return (
      <View css="flex-direction: row; align-items: center;">
        <View
          style={{
            width: rpx(7),
            height: rpx(7),
            borderRadius: rpx(4),
            backgroundColor: color,
            marginRight: rpx(6),
          }}
        />
        <Text
          style={[
            { color, fontSize: rpx(16), lineHeight: rpx(20) },
            labelStyle,
          ]}
        >
          {label}
        </Text>
      </View>
    );
  },
);
